// Support-portal calls for the Patient Prospects queue. Mirrors the
// existing `getSPDataWithParams` / `postSPDataWithParams` pattern used
// across supportPortal so auth headers + locale are consistent.
//
// Backend contracts: docs/PATIENT_PROSPECT_SIGNUP_PLAN.md §6.6 + §6.5.
//
// Note: `supportApiURL` is the host only — the URL passed to these
// helpers must include the full `/api/secure/...` path.

import {
  getSPDataWithParams,
  postSPDataWithParams,
} from 'src/supportPortal/services/services'

const PROSPECT_BASE = '/api/secure/cc/support/patient-prospect'

/**
 * GET /api/secure/cc/support/patient-prospect/list
 * Returns axios response; payload at `res.data` is
 * `{ items, totalCount, page, size }`.
 */
export async function listProspects({ status, pathway, specialtyTag, q, page = 0, size = 20 } = {}) {
  const params = {}
  if (status) params.status = status
  if (pathway) params.pathway = pathway
  if (specialtyTag) params.specialtyTag = specialtyTag
  if (q) params.q = q
  params.page = page
  params.size = size
  return getSPDataWithParams(`${PROSPECT_BASE}/list`, params)
}

/** GET /api/secure/cc/support/patient-prospect/{userId} — full bundle. */
export async function getProspect(userId) {
  return getSPDataWithParams(`${PROSPECT_BASE}/${encodeURIComponent(userId)}`, {})
}

/**
 * GET /api/secure/cc/support/patient-prospect/{userId}/reports/{reportId}/url
 * Returns a short-lived S3 presigned URL the browser can open directly.
 */
export async function getReportDownloadUrl(userId, reportId) {
  return getSPDataWithParams(
    `${PROSPECT_BASE}/${encodeURIComponent(userId)}/reports/${encodeURIComponent(reportId)}/url`,
    {},
  )
}

/**
 * POST /api/secure/cc/support/patient-prospect/{userId}/promote
 * Body: { clinicBrandId, mentorId?, notes? }
 */
export async function promoteProspect(userId, body) {
  return postSPDataWithParams(`${PROSPECT_BASE}/${encodeURIComponent(userId)}/promote`, body, {})
}

/**
 * POST /api/secure/cc/support/patient-prospect/{userId}/reject
 * Soft-revoke per D3 — body: { reason }
 */
export async function rejectProspect(userId, body) {
  return postSPDataWithParams(`${PROSPECT_BASE}/${encodeURIComponent(userId)}/reject`, body, {})
}

/**
 * POST /api/secure/patient/{patientInfoId}/validate
 * Marks a PatientInfo as identity-validated. Different controller path
 * (no /cc/ prefix) — auth gate is on the backend, callable by clinical
 * roles + support roles alike.
 */
export async function validatePatientInfo(patientInfoId, body) {
  return postSPDataWithParams(
    `/api/secure/patient/${encodeURIComponent(patientInfoId)}/validate`,
    body || {},
    {},
  )
}
